import { Link } from "react-router-dom";
import type { ExperienceDetail } from "@/mocks/experienceDetails";
import type { ExperienceFacets } from "@/mocks/experienceCatalog";

interface ExperienceCardItem {
  slug: string;
  emoji: string;
  title: string;
  tagline: string;
  description: string;
  highlights: string[];
  perfectFor: string;
}

interface ExperienceListCardProps {
  experience: ExperienceCardItem;
  detail?: ExperienceDetail;
  facets?: ExperienceFacets;
}

export default function ExperienceListCard({ experience, detail, facets }: ExperienceListCardProps) {
  const image = detail?.heroImage;

  return (
    <div className="group h-full bg-background-50 rounded-lg border border-background-200 overflow-hidden flex flex-col hover:border-primary-300 transition-colors">
      <Link
        to={`/experiences/${experience.slug}`}
        className="block relative w-full h-48 overflow-hidden bg-background-200 cursor-pointer"
      >
        {image && (
          <img
            src={image}
            width={1200}
            height={800}
            alt={`${experience.title} — ${experience.tagline} farm experience in Konaseema, Andhra Pradesh`}
            title={`${experience.title} - Rythugaru experience`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            decoding="async"
          />
        )}
        <span className="absolute top-4 left-4 text-2xl w-11 h-11 flex items-center justify-center rounded-full bg-background-50/95">
          {experience.emoji}
        </span>
        {detail?.badge && (
          <span className="absolute top-4 right-4 bg-accent-500 text-background-50 text-[11px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
            {detail.badge}
          </span>
        )}
      </Link>

      <div className="p-6 flex flex-col flex-1">
        <Link to={`/experiences/${experience.slug}`} className="cursor-pointer">
          <h3 className="font-heading text-xl text-foreground-950 font-semibold leading-snug group-hover:text-primary-700 transition-colors">
            {experience.title}
          </h3>
        </Link>
        <p className="text-xs font-semibold uppercase tracking-wider text-accent-600 mt-2">
          {experience.tagline}
        </p>
        <p className="text-sm text-foreground-600 leading-relaxed mt-4 flex-1">
          {experience.description}
        </p>

        {facets && (
          <div className="flex flex-wrap gap-2 mt-5">
            {facets.durations.map((d) => (
              <span
                key={d}
                className="text-[11px] font-medium bg-primary-100 text-primary-800 px-2.5 py-1 rounded-full capitalize"
              >
                {d === "half" ? "Half day" : d === "full" ? "Full day" : "Multi-day"}
              </span>
            ))}
            {facets.seasons.map((s) => (
              <span
                key={s}
                className="text-[11px] font-medium bg-secondary-100 text-secondary-800 px-2.5 py-1 rounded-full capitalize"
              >
                {s}
              </span>
            ))}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-background-200 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <i className="ri-user-follow-line text-primary-500 shrink-0"></i>
            <span className="text-xs font-medium text-foreground-700 truncate">
              {experience.perfectFor}
            </span>
          </span>
          <Link
            to={`/experiences/${experience.slug}`}
            className="flex items-center gap-1 text-sm font-semibold text-primary-700 whitespace-nowrap shrink-0 cursor-pointer"
          >
            View
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
          </Link>
        </div>

        <Link
          to={`/experiences/${experience.slug}#booking-calendar`}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-5 py-3 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-calendar-check-line text-lg"></i>
          Reserve Your Date
        </Link>
      </div>
    </div>
  );
}